import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import Login from "./pages/Login";

const AssessmentDetail = lazy(() => import("./pages/AssessmentDetail"));
const Audits = lazy(() => import("./pages/Audits"));
const AuditWorkspace = lazy(() => import("./pages/AuditWorkspace"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const FindingDetail = lazy(() => import("./pages/FindingDetail"));
const Findings = lazy(() => import("./pages/Findings"));
const HelpGuide = lazy(() => import("./pages/HelpGuide"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const NewAudit = lazy(() => import("./pages/NewAudit"));
const AuditReport = lazy(() => import("./pages/AuditReport"));
const SystemStatus = lazy(() => import("./pages/SystemStatus"));
const Requirements = lazy(() => import("./pages/RequirementsLibrary").then((module) => ({ default: module.Requirements })));
const RequirementDetail = lazy(() => import("./pages/RequirementsLibrary").then((module) => ({ default: module.RequirementDetail })));
const Documents = lazy(() => import("./pages/DocumentsLibrary").then((module) => ({ default: module.Documents })));
const DocumentDetail = lazy(() => import("./pages/DocumentsLibrary").then((module) => ({ default: module.DocumentDetail })));
const Traceability = lazy(() => import("./pages/TraceabilityView").then((module) => ({ default: module.Traceability })));
const Knowledge = lazy(() => import("./pages/KnowledgeView").then((module) => ({ default: module.Knowledge })));
const Administration = lazy(() => import("./pages/AdminView").then((module) => ({ default: module.Administration })));
const ConfluenceIntakeReview = lazy(() => import("./pages/ConfluenceIntakeReview"));
const DiagnosticSandbox = lazy(() => import("./pages/DiagnosticSandbox"));
const ExperimentalWorkspace = lazy(() => import("./pages/ExperimentalWorkspace"));

function useAuth() {
  const [auth, setAuth] = useState<{ loading: boolean; user: string | null; mode: string | null }>({
    loading: true, user: null, mode: null,
  });
  useEffect(() => {
    fetch("/api/auth/config")
      .then((r) => r.json())
      .then((cfg) => {
        if (cfg.mode === "local") {
          setAuth({ loading: false, user: "local", mode: "local" });
          return;
        }
        fetch("/api/auth/me", { credentials: "include" }).then((r) => {
          if (r.ok) return r.json().then((d) => setAuth({ loading: false, user: d.user, mode: cfg.mode }));
          setAuth({ loading: false, user: null, mode: cfg.mode });
        }).catch(() => setAuth({ loading: false, user: null, mode: cfg.mode }));
      })
      .catch(() => setAuth({ loading: false, user: null, mode: null }));
  }, []);
  return auth;
}

export default function App() {
  const { loading, user, mode } = useAuth();
  if (loading) return <div style={{ color: "#94a3b8", padding: 40, background: "#0f1117", minHeight: "100vh" }}>Loading...</div>;
  if (mode === "session" && !user) return <Login />;
  return <AppShell readOnly={mode === "session"}><Suspense fallback={<div className="state-line">Loading page...</div>}><Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/help" element={<HelpGuide />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/audits" element={<Audits />} />
      <Route path="/audits/new" element={<NewAudit />} />
      <Route path="/audits/:auditId" element={<AuditWorkspace />} />
      <Route path="/audits/:auditId/requirements" element={<AuditWorkspace />} />
      <Route path="/audits/:auditId/requirements/:requirementId" element={<AssessmentDetail />} />
      <Route path="/audits/:auditId/report" element={<AuditReport />} />
      <Route path="/findings" element={<Findings />} />
      <Route path="/findings/:findingId" element={<FindingDetail />} />
      <Route path="/requirements" element={<Requirements />} />
      <Route path="/requirements/:requirementId" element={<RequirementDetail />} />
      <Route path="/documents" element={<Documents />} />
      <Route path="/confluence-intake" element={<ConfluenceIntakeReview />} />
      <Route path="/diagnostic-sandbox" element={<DiagnosticSandbox />} />
      <Route path="/experimental" element={<ExperimentalWorkspace />} />
      <Route path="/documents/:documentId" element={<DocumentDetail />} />
      <Route path="/traceability" element={<Traceability />} />
      <Route path="/knowledge" element={<Knowledge />} />
      <Route path="/administration" element={<Administration />} />
      <Route path="/system" element={<SystemStatus />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes></Suspense></AppShell>;
}
